import React, { useContext } from "react";
import AvatarSLink from "../../components/AvatarSLink";
import { CenterContext, ClassesContext, StudentsContext } from "../../../../Context";
import Rating from "../../components/Rating";
import Info from "../../components/Info";
import MultiLine from "../../components/MultiLine";
import { Copy } from "lucide-react";
import SCheckBox from "../../components/SCheckBox";
import mut from "../../hooks/mut";
import { copyToClipboard } from "../../hooks/copyToClipboard";
import { format } from "date-fns";
import Text from "../../components/Text";
import { usePrimaryColor } from "../../components/PrimaryColorContext";


/**
 * StudentFeedback - Individual student feedback row with scores, homework, notes, and copy
 * @param {object} props
 * @param {object} props.attendance - Attendance record for this student
 * @param {function} props.setLesson - State setter for lesson
 * @returns {JSX.Element}
 * @author opencode (glm-5.1) + Forge
 * @version 2.0.0
 * @since 2026-04-13
 */
export default function StudentFeedback(props) {
  const { attendance, setLesson } = props;
  const { findStudent } = useContext(StudentsContext);
  const { primaryColor } = usePrimaryColor();
  const { findClass } = useContext(ClassesContext);

  const copy = (id) => {
    copyToClipboard(paintFeedback, 400, 1000);
    mut("post", `/teachers-feedback/${id}`, { feedbackSent: new Date() });
  };

  function paintFeedback(ctx) {
    const student = findStudent(attendance.student);
    const htClass = findClass(attendance.htClass);
    const margin = 20;
    let line = 30;
    ctx.font = "24px arial";
    ctx.fillStyle = "black";
    ctx.fillText(student.name, margin, 34);
    ctx.font = "16px arial";
    ctx.fillStyle = "gray";
    line += 30;
    ctx.fillText(`${htClass.name}`, margin, line);
    line += 20;
    ctx.fillText(`${format(new Date(attendance.classDate), "dd/MM/yyyy")}`, margin, line);
    line += 30;
    ctx.font = "12px arial";
    ctx.fillStyle = "black";
    for (let item in attendance.feedback) {
      ctx.fillText(item, margin, line);
      line += 5;
      let oneImage = document.getElementById(`score${attendance.feedback[item]}`);
      ctx.drawImage(oneImage, margin, line);
      line += 60;
    }
    line += 10;
    if (attendance.notes) {
      ctx.font = "bold 14px arial";
      ctx.fillText("Nhận xét buổi học", margin, line);
      line += 20;
      ctx.font = "14px arial";
      line = wrapText(ctx, attendance.notes, margin, line, 360, 24);
      line += 20;
    }

    line += 10;
    if (attendance.homeWorkNotes) {
      ctx.font = "bold 14px arial";
      ctx.fillText("Bài tập về nhà", margin, line);
      line += 20;
      ctx.font = "14px arial";
      line = wrapText(ctx, attendance.homeWorkNotes, margin, line, 360, 24);
      line += 20;
    }

    return line + 20;
  }

  if (attendance.attendance > 1)
    return (
      <div className="flex items-center gap-2 text-slate-400 py-1">
        <AvatarSLink id={attendance.student} />
        <span className="text-sm">Absent</span>
      </div>
    );

  return (
    <div className="rounded-[10px] border border-slate-200/80 bg-white p-3 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
      {/* Hidden score images for canvas painting */}
      <img src="/system/score5.png" alt="5" id="score5" className="hidden" />
      <img src="/system/score4.png" alt="4" id="score4" className="hidden" />
      <img src="/system/score3.png" alt="3" id="score3" className="hidden" />
      <img src="/system/score2.png" alt="2" id="score2" className="hidden" />
      <img src="/system/score1.png" alt="1" id="score1" className="hidden" />

      {/* Header row */}
      <div className="flex justify-between items-center pb-2">
        <AvatarSLink id={attendance.student} />
        <div className="flex items-center gap-2">
          <Text
            value={attendance.miniTestScore}
            setValue={(miniTestScore) =>
              setLesson((prev) => {
                let att = prev.attendance;
                let at = att.find((a) => a.student === attendance.student);
                if (miniTestScore > 10) {
                  at.miniTestScore = 10;
                  return { ...prev, attendance: att };
                }
                if (miniTestScore < 0) {
                  at.miniTestScore = 0;
                  return { ...prev, attendance: att };
                }
                at.miniTestScore = miniTestScore;
                return { ...prev, attendance: att };
              })
            }
            type="number"
            url={`/attendance/${attendance._id}`}
            field="miniTestScore"
            size="small"
            label="Mini test score"
            style={{ width: 150 }}
          />
          <SCheckBox
            value={
              attendance.homeworkCompleted &&
              new Date(attendance.homeworkCompleted) > new Date(0, 0, 0)
                ? true
                : false
            }
            label="Homework completed"
            setValue={(e) =>
              setLesson((prev) => {
                let at = prev.attendance.find(
                  (a) => a._id === attendance._id
                );
                attendance.homeworkCompleted = e;
                let update = e
                  ? { homeworkCompleted: new Date() }
                  : { $unset: { homeworkCompleted: "" } };
                mut("post", `/attendance/${at._id}`, update);
                return {
                  ...prev,
                  attendance: prev.attendance.map((a) =>
                    a._id === at._id ? at : a
                  ),
                };
              })
            }
          />
          <Text
            value={attendance.homeworkScore}
            setValue={(homeworkScore) =>
              setLesson((prev) => {
                let att = prev.attendance;
                let at = att.find((a) => a.student === attendance.student);
                if (homeworkScore > 10) {
                  at.homeworkScore = 10;
                  return { ...prev, attendance: att };
                }
                if (homeworkScore < 0) {
                  at.homeworkScore = 0;
                  return { ...prev, attendance: att };
                }
                at.homeworkScore = homeworkScore;
                return { ...prev, attendance: att };
              })
            }
            type="number"
            url={`/attendance/${attendance._id}`}
            field="homeworkScore"
            size="small"
            label="Score"
            style={{ width: 70 }}
          />
          <SCheckBox
            value={
              attendance.feedbackComplete &&
              new Date(attendance.feedbackComplete) > new Date(0, 0, 0)
                ? true
                : false
            }
            label="Voice feedback completed"
            setValue={(e) =>
              setLesson((prev) => {
                let at = prev.attendance.find(
                  (a) => a._id === attendance._id
                );
                attendance.feedbackComplete = e;
                let update = e
                  ? { feedbackComplete: new Date() }
                  : { $unset: { feedbackComplete: "" } };
                mut("post", `/attendance/${at._id}`, update);
                return {
                  ...prev,
                  attendance: prev.attendance.map((a) =>
                    a._id === at._id ? at : a
                  ),
                };
              })
            }
          />
          <button
            onClick={() => copy(attendance._id)}
            className={`p-1.5 rounded-[10px] text-slate-400 hover:text-${primaryColor}-800 hover:bg-slate-50 transition-all duration-150 cursor-pointer`}
            title="Copy feedback"
          >
            <Copy size={16} />
          </button>
        </div>
      </div>

      {/* Feedback body */}
      <div className="flex gap-3 pt-1">
        <Stats
          feedback={attendance.feedback}
          id={attendance._id}
          setLesson={setLesson}
        />
        <MultiLine
          value={attendance.notes}
          setValue={(notes) =>
            setLesson((prev) => {
              let att = prev.attendance;
              let at = att.find((a) => a._id === attendance._id);
              at.notes = notes;
              return { ...prev, attendance: att };
            })
          }
          rows={7}
          label="Nhận xét buổi học"
          fontSize={14}
          url={`/teachers-feedback/${attendance._id}`}
          field="notes"
          style={{ height: 174 }}
        />
        <MultiLine
          value={attendance.homeWorkNotes}
          rows={7}
          label="Bài tập về nhà"
          fontSize={14}
          url={`/teachers-feedback/${attendance._id}`}
          field="homeWorkNotes"
          setValue={(homeWorkNotes) =>
            setLesson((prev) => {
              let att = prev.attendance;
              let at = att.find((a) => a._id === attendance._id);
              at.homeWorkNotes = homeWorkNotes;
              return { ...prev, attendance: att };
            })
          }
          style={{ height: 174 }}
        />
      </div>
    </div>
  );
}

/**
 * Stats - Rating grid for feedback categories
 * @param {object} props
 * @param {object} props.feedback - Feedback scores map
 * @param {string} props.id - Attendance record ID
 * @param {function} props.setLesson - State setter for lesson
 * @returns {JSX.Element}
 * @author opencode (glm-5.1) + Forge
 * @version 2.0.0
 * @since 2026-04-13
 */
function Stats(props) {
  const { id, feedback, setLesson } = props;
  const { center } = useContext(CenterContext);
  const { feedbackCategories } = center;

  return (
    <div className="p-3 border border-slate-200 rounded-[10px] space-y-1">
      {feedbackCategories.map((f) => (
        <Info
          key={f}
          title={f}
          body={
            <Rating
              value={feedback && feedback[f] ? feedback[f] : 0}
              url={`/teachers-feedback-stats/${id}`}
              field={`${f}`}
              setValue={(score) =>
                setLesson((prev) => {
                  let attendance = prev.attendance;
                  let at = attendance.find((a) => a._id === id);
                  if (!at.feedback) at.feedback = {};
                  at.feedback[f] = score;
                  return { ...prev, attendance };
                })
              }
            />
          }
        />
      ))}
    </div>
  );
}

/**
 * wrapText - Canvas text wrapping utility for feedback image generation
 * @param {CanvasRenderingContext2D} context
 * @param {string} text
 * @param {number} x
 * @param {number} y
 * @param {number} maxWidth
 * @param {number} lineHeight
 * @returns {number} Final y position
 */
function wrapText(context, text, x, y, maxWidth, lineHeight) {
  var words = text.split(" ");
  var line = "";

  for (var n = 0; n < words.length; n++) {
    var testLine = line + words[n] + " ";
    var metrics = context.measureText(testLine);
    var testWidth = metrics.width;
    if (testWidth > maxWidth && n > 0) {
      context.fillText(line, x, y);
      line = words[n] + " ";
      y += lineHeight;
    } else {
      line = testLine;
    }
  }
  context.fillText(line, x, y);
  return y;
}
